import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Chip,
  ButtonGroup,
  IconButton,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import {
  idoraforditasService,
  MunkaszamOsszesitoDto,
  IdoraforditasDto,
  MunkaszamResztvevoOsszesitoDto,
  WORK_LOCATION_LABELS,
  WorkLocationType
} from '../services/idoraforditasService';
import { apiService, getApiErrorMessage } from '../services/apiService';
import { authService } from '../services/authService';
import { permissionService, Permissions } from '../services/permissionService';
import DetailPanel from './DetailPanel';
import ControllerWidget from './ControllerWidget';
import AddIdoraforditasDialog from './AddIdoraforditasDialog';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

type ViewMode = 'munkaszamok' | 'resztvevok' | 'idoraforditasok' | 'heti' | 'havi';

interface IdoraforditasokContentProps {
  onAddRecord?: () => void;
  initialMunkaszam?: string | null;
  initialParticipant?: string | null;
}

const IdoraforditasokContent: React.FC<IdoraforditasokContentProps> = ({ 
  onAddRecord, 
  initialMunkaszam,
  initialParticipant 
}) => {
  const padLeft = (input: number | string, length: number): string => {
    const text = String(input);
    if (text.length >= length) {
      return text;
    }

    return `${new Array(length - text.length + 1).join('0')}${text}`;
  };

  const [viewMode, setViewMode] = useState<ViewMode>('munkaszamok');
  const [munkaszamok, setMunkaszamok] = useState<MunkaszamOsszesitoDto[]>([]);
  const [filteredMunkaszamok, setFilteredMunkaszamok] = useState<MunkaszamOsszesitoDto[]>([]);
  const [munkaszamResztvevok, setMunkaszamResztvevok] = useState<MunkaszamResztvevoOsszesitoDto[]>([]);
  const [filteredMunkaszamResztvevok, setFilteredMunkaszamResztvevok] = useState<MunkaszamResztvevoOsszesitoDto[]>([]);
  const [idoraforditasok, setIdoraforditasok] = useState<IdoraforditasDto[]>([]);
  const [filteredIdoraforditasok, setFilteredIdoraforditasok] = useState<IdoraforditasDto[]>([]);
  const [selectedMunkaszam, setSelectedMunkaszam] = useState<string | null>(null);
  const [selectedParticipant, setSelectedParticipant] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [searchFilter, setSearchFilter] = useState<{column: string, value: string} | null>(null);
  const [tableColumns, setTableColumns] = useState<string[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<string[] | undefined>(undefined);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [periodAnchorDate, setPeriodAnchorDate] = useState<Date>(new Date());
  const [includeAllUsers, setIncludeAllUsers] = useState(false);
  const [canViewAllTimeTracking, setCanViewAllTimeTracking] = useState(
    permissionService.hasPermission(Permissions.ViewAllTimeTracking)
  );
  const [expandedDayKeys, setExpandedDayKeys] = useState<Record<string, boolean>>({});

  const getDetailedViewColumns = (columns: string[]): string[] => {
    const hiddenColumns = ['id', 'munkaszamId', 'userId', 'felhasznaloNev'];
    return columns.filter((column) => hiddenColumns.indexOf(column) === -1);
  };

  const getResztvevoViewColumns = (columns: string[]): string[] => {
    const hiddenColumns = ['userId'];
    return columns.filter((column) => hiddenColumns.indexOf(column) === -1);
  };

  const normalizeMunkaszamKey = (value: string | null | undefined): string => {
    const raw = (value || '').trim().replace(/\s+/g, '');
    const match = raw.match(/^(\d{2,4})-(\d{1,3})\/(\d{1,})$/);
    if (!match) {
      return raw.toUpperCase();
    }

    const year = Number(match[1]) % 100;
    const sorszam = Number(match[2]);
    const torzsszam = Number(match[3]);
    return `${padLeft(year, 2)}-${padLeft(sorszam, 3)}/${padLeft(torzsszam, 4)}`;
  };

  const sortMunkaszamOsszesitokByMunkaszamTabOrder = async (
    items: MunkaszamOsszesitoDto[]
  ): Promise<MunkaszamOsszesitoDto[]> => {
    try {
      const munkaszamokData = await apiService.getData('munkaszamok');
      const orderByMunkaszam = new Map<string, number>();

      (Array.isArray(munkaszamokData) ? munkaszamokData : []).forEach((item: any, index: number) => {
        const key = normalizeMunkaszamKey(String(item?.munkaszam || ''));
        if (key && !orderByMunkaszam.has(key)) {
          orderByMunkaszam.set(key, index);
        }
      });

      return [...items].sort((a, b) => {
        const aOrder = orderByMunkaszam.get(normalizeMunkaszamKey(a.munkaszam));
        const bOrder = orderByMunkaszam.get(normalizeMunkaszamKey(b.munkaszam));

        if (aOrder != null && bOrder != null) {
          return aOrder - bOrder;
        }
        if (aOrder != null) {
          return -1;
        }
        if (bOrder != null) {
          return 1;
        }

        return b.munkaszam.localeCompare(a.munkaszam, 'hu-HU', { numeric: true, sensitivity: 'base' });
      });
    } catch {
      return [...items].sort((a, b) =>
        b.munkaszam.localeCompare(a.munkaszam, 'hu-HU', { numeric: true, sensitivity: 'base' })
      );
    }
  };

  useEffect(() => {
    const refreshPermission = () => {
      const hasPermission = permissionService.hasPermission(Permissions.ViewAllTimeTracking);
      setCanViewAllTimeTracking(hasPermission);
      if (!hasPermission) {
        setIncludeAllUsers(false);
      }
    };

    refreshPermission();
    const unsubscribe = permissionService.onPermissionsChanged(refreshPermission);
    loadMunkaszamok();
    return unsubscribe;
  }, []);

  // Ha van initialMunkaszam és initialParticipant, betöltjük az időráfordításokat
  useEffect(() => {
    if (initialMunkaszam && initialParticipant) {
      loadIdoraforditasokByParticipantAndMunkaszam(initialParticipant, initialMunkaszam);
    } else if (initialMunkaszam) {
      loadIdoraforditasok(initialMunkaszam);
    }
  }, [initialMunkaszam, initialParticipant]);

  // Keresési szűrő alkalmazása
  useEffect(() => {
    if (viewMode === 'munkaszamok') {
      if (!searchFilter || !searchFilter.value) {
        setFilteredMunkaszamok(munkaszamok);
      } else {
        const filtered = munkaszamok.filter(item => {
          if (!searchFilter.column || searchFilter.column === '') {
            return Object.keys(item).some(key => {
              const value = (item as any)[key];
              if (value == null) return false;
              return value.toString().toLowerCase().includes(searchFilter.value.toLowerCase());
            });
          }
          const cellValue = (item as any)[searchFilter.column];
          if (cellValue == null) return false;
          return cellValue.toString().toLowerCase().includes(searchFilter.value.toLowerCase());
        });
        setFilteredMunkaszamok(filtered);
      }
    } else if (viewMode === 'resztvevok') {
      if (!searchFilter || !searchFilter.value) {
        setFilteredMunkaszamResztvevok(munkaszamResztvevok);
      } else {
        const filtered = munkaszamResztvevok.filter(item => {
          if (!searchFilter.column || searchFilter.column === '') {
            return Object.keys(item).some(key => {
              const value = (item as any)[key];
              if (value == null) return false;
              return value.toString().toLowerCase().includes(searchFilter.value.toLowerCase());
            });
          }
          const cellValue = (item as any)[searchFilter.column];
          if (cellValue == null) return false;
          return cellValue.toString().toLowerCase().includes(searchFilter.value.toLowerCase());
        });
        setFilteredMunkaszamResztvevok(filtered);
      }
    } else {
      if (!searchFilter || !searchFilter.value) {
        setFilteredIdoraforditasok(idoraforditasok);
      } else {
        const filtered = idoraforditasok.filter(item => {
          if (!searchFilter.column || searchFilter.column === '') {
            return Object.keys(item).some(key => {
              const value = (item as any)[key];
              if (value == null) return false;
              return value.toString().toLowerCase().includes(searchFilter.value.toLowerCase());
            });
          }
          const cellValue = (item as any)[searchFilter.column];
          if (cellValue == null) return false;
          return cellValue.toString().toLowerCase().includes(searchFilter.value.toLowerCase());
        });
        setFilteredIdoraforditasok(filtered);
      }
    }
  }, [searchFilter, munkaszamok, munkaszamResztvevok, idoraforditasok, viewMode]);

  // Oszlopok frissítése amikor adatok változnak
  useEffect(() => {
    if (viewMode === 'munkaszamok' && munkaszamok.length > 0) {
      setTableColumns(Object.keys(munkaszamok[0]));
      setFilteredMunkaszamok(munkaszamok);
    } else if (viewMode === 'resztvevok') {
      if (munkaszamResztvevok.length > 0) {
        setTableColumns(Object.keys(munkaszamResztvevok[0]));
      } else {
        setTableColumns(['felhasznaloNev', 'osszesOraszam', 'utolsoIdoraforditasDatum', 'utolsoComment']);
      }
      setFilteredMunkaszamResztvevok(munkaszamResztvevok);
    } else if (viewMode === 'idoraforditasok') {
      if (idoraforditasok.length > 0) {
        const columns = getDetailedViewColumns(Object.keys(idoraforditasok[0]));
        // Biztosítjuk hogy a workLocation és workType oszlopok benne legyenek
        if (!columns.includes('workLocation')) columns.push('workLocation');
        if (!columns.includes('workType')) columns.push('workType');
        setTableColumns(columns);
      } else {
        // Ha nincsenek adatok, alapértelmezett oszlopok
        setTableColumns(['datum', 'bejegyzesTargya', 'idoraforditasErteke', 'workLocation', 'workType']);
      }
      setFilteredIdoraforditasok(idoraforditasok);
    } else if (viewMode === 'heti' || viewMode === 'havi') {
      if (idoraforditasok.length > 0) {
        const columns = Object.keys(idoraforditasok[0]);
        // Biztosítjuk hogy a workLocation és workType oszlopok benne legyenek
        if (!columns.includes('workLocation')) columns.push('workLocation');
        if (!columns.includes('workType')) columns.push('workType');
        setTableColumns(columns);
      } else {
        const baseColumns = includeAllUsers
          ? ['datum', 'munkaszamErteke', 'bejegyzesTargya', 'idoraforditasErteke', 'workLocation', 'workType', 'felhasznaloNev']
          : ['datum', 'munkaszamErteke', 'bejegyzesTargya', 'idoraforditasErteke', 'workLocation', 'workType'];
        setTableColumns(baseColumns);
      }
      setFilteredIdoraforditasok(idoraforditasok);
    }
  }, [munkaszamok, munkaszamResztvevok, idoraforditasok, viewMode, includeAllUsers]);

  useEffect(() => {
    if (viewMode === 'heti' || viewMode === 'havi') {
      loadPeriodData(viewMode, periodAnchorDate);
    }
  }, [viewMode, periodAnchorDate, includeAllUsers]);

  useEffect(() => {
    if (viewMode === 'heti' || viewMode === 'havi') {
      setExpandedDayKeys({});
    }
  }, [viewMode, periodAnchorDate, includeAllUsers]);

  useEffect(() => {
    if (viewMode === 'munkaszamok') {
      loadMunkaszamok();
    }
  }, [includeAllUsers, canViewAllTimeTracking]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (column: string, searchValue: string) => {
    if (searchValue.trim()) {
      setSearchFilter({ column, value: searchValue });
    } else {
      setSearchFilter(null);
    }
  };

  const handleGetSuggestions = async (searchValue: string): Promise<string[]> => {
    const data =
      viewMode === 'munkaszamok'
        ? munkaszamok
        : viewMode === 'resztvevok'
          ? munkaszamResztvevok
          : idoraforditasok;
    
    if (!data || data.length === 0 || searchValue.length < 2) {
      return [];
    }

    const lowerSearchValue = searchValue.toLowerCase();
    const uniqueValues = new Set<string>();

    data.forEach(item => {
      Object.keys(item).forEach(key => {
        const value = (item as any)[key];
        if (value != null) {
          const stringValue = value.toString();
          if (stringValue.toLowerCase().includes(lowerSearchValue)) {
            uniqueValues.add(stringValue);
          }
        }
      });
    });

    return Array.from(uniqueValues)
      .sort((a, b) => {
        const aStarts = a.toLowerCase().startsWith(lowerSearchValue);
        const bStarts = b.toLowerCase().startsWith(lowerSearchValue);
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;
        return a.localeCompare(b);
      })
      .slice(0, 10);
  };

  const handleAddRecord = () => {
    setAddDialogOpen(true);
  };

  const handleDialogClose = () => {
    setAddDialogOpen(false);
  };

  const handleDialogSuccess = async () => {
    setAddDialogOpen(false);
    await reloadCurrentView();
  };

  const handleEdit = async (updatedData: any) => {
    try {
      await idoraforditasService.updateIdoraforditas(updatedData.id, updatedData);
      await reloadCurrentView();
      setIsPanelOpen(false);
    } catch (error) {
      console.error('Hiba az időráfordítás frissítése során:', error);
      setError(getApiErrorMessage(error, 'Hiba az időráfordítás frissítése során.'));
    }
  };

  const handleDelete = async () => {
    if (!selectedItem?.id) {
      console.error('Nincs selectedItem.id értéke:', selectedItem);
      return;
    }
    console.log('Törlés megkezdése, ID:', selectedItem.id, 'Típus:', typeof selectedItem.id);
    try {
      await idoraforditasService.deleteIdoraforditas(selectedItem.id);
      await reloadCurrentView();
      setIsPanelOpen(false);
      setSelectedItem(null);
    } catch (error: any) {
      console.error('Hiba az időráfordítás törlése során:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        method: error.config?.method
      });
      setError(getApiErrorMessage(error, 'Hiba az időráfordítás törlése során.'));
    }
  };

  const loadMunkaszamok = async () => {
    setLoading(true);
    setError(null);
    setSearchFilter(null);
    setVisibleColumns(undefined);
    setMunkaszamResztvevok([]);
    setFilteredMunkaszamResztvevok([]);
    try {
      const user = authService.getCurrentUser();
      if (!user || !user.userId) {
        setError('Nem található felhasználói azonosító');
        return;
      }

      const loadAllUsersData = canViewAllTimeTracking && includeAllUsers;
      const data = loadAllUsersData
        ? await idoraforditasService.getMindenMunkaszamOsszesito()
        : await idoraforditasService.getMunkaszamokByUserId(user.userId);

      const sortedData = await sortMunkaszamOsszesitokByMunkaszamTabOrder(data);

      setMunkaszamok(sortedData);
      setViewMode('munkaszamok');
      setSelectedMunkaszam(null);
      setSelectedParticipant(null);
      setIdoraforditasok([]);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Hiba történt a munkaszámok betöltése során.'));
    } finally {
      setLoading(false);
    }
  };

  const loadIdoraforditasok = async (munkaszam: string) => {
    setLoading(true);
    setError(null);
    setSearchFilter(null);
    setVisibleColumns(undefined);
    try {
      const user = authService.getCurrentUser();
      if (!user || !user.userId) {
        setError('Nem található felhasználói azonosító');
        return;
      }

      const data = await idoraforditasService.getIdoraforditasokByUserIdAndMunkaszam(user.userId, munkaszam);
      setIdoraforditasok(data);
      setMunkaszamResztvevok([]);
      setSelectedMunkaszam(munkaszam);
      setSelectedParticipant(null);
      setViewMode('idoraforditasok');
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Hiba történt az időráfordítások betöltése során.'));
    } finally {
      setLoading(false);
    }
  };

  const loadMunkaszamResztvevok = async (munkaszam: string) => {
    setLoading(true);
    setError(null);
    setSearchFilter(null);
    setVisibleColumns(undefined);
    try {
      const data = await idoraforditasService.getMunkaszamResztvevokByMunkaszam(munkaszam);
      setMunkaszamResztvevok(data);
      setFilteredMunkaszamResztvevok(data);
      setIdoraforditasok([]);
      setSelectedMunkaszam(munkaszam);
      setSelectedParticipant(null);
      setViewMode('resztvevok');
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Hiba történt a munkaszám résztvevőinek betöltése során.'));
    } finally {
      setLoading(false);
    }
  };

  const loadIdoraforditasokByParticipantAndMunkaszam = async (participantName: string, munkaszam: string) => {
    setLoading(true);
    setError(null);
    setSearchFilter(null);
    setVisibleColumns(undefined);
    try {
      const data = await idoraforditasService.getIdoraforditasokByParticipantAndMunkaszam(participantName, munkaszam);
      setIdoraforditasok(data);
      setMunkaszamResztvevok([]);
      setSelectedMunkaszam(munkaszam);
      setSelectedParticipant(participantName);
      setViewMode('idoraforditasok');
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Hiba történt az időráfordítások betöltése során.'));
    } finally {
      setLoading(false);
    }
  };

  const formatDateToApi = (date: Date): string => {
    const year = date.getFullYear();
    const month = padLeft(date.getMonth() + 1, 2);
    const day = padLeft(date.getDate(), 2);
    return `${year}-${month}-${day}`;
  };

  const getWeekBounds = (anchor: Date): { start: Date; end: Date } => {
    const start = new Date(anchor);
    const dayOfWeek = start.getDay();
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    start.setDate(start.getDate() - daysFromMonday);
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);

    return { start, end };
  };

  const getMonthBounds = (anchor: Date): { start: Date; end: Date } => {
    const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    start.setHours(0, 0, 0, 0);

    const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    end.setHours(23, 59, 59, 999);

    return { start, end };
  };

  const loadPeriodData = async (mode: 'heti' | 'havi', anchorDate: Date) => {
    setLoading(true);
    setError(null);
    setSearchFilter(null);
    setVisibleColumns(undefined);

    try {
      const { start, end } = mode === 'heti' ? getWeekBounds(anchorDate) : getMonthBounds(anchorDate);

      const data = await idoraforditasService.getIdoraforditasokByDateRange(
        formatDateToApi(start),
        formatDateToApi(end),
        {
          includeAllUsers: canViewAllTimeTracking && includeAllUsers,
        }
      );

      setIdoraforditasok(data);
      setSelectedMunkaszam(null);
      setSelectedParticipant(null);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Hiba történt az időráfordítások betöltése során.'));
    } finally {
      setLoading(false);
    }
  };

  const reloadCurrentView = async () => {
    if (viewMode === 'idoraforditasok' && selectedMunkaszam) {
      if (selectedParticipant) {
        await loadIdoraforditasokByParticipantAndMunkaszam(selectedParticipant, selectedMunkaszam);
      } else {
        await loadIdoraforditasok(selectedMunkaszam);
      }
      return;
    }

    if (viewMode === 'munkaszamok') {
      await loadMunkaszamok();
      return;
    }

    if (viewMode === 'resztvevok' && selectedMunkaszam) {
      await loadMunkaszamResztvevok(selectedMunkaszam);
      return;
    }

    if (viewMode === 'heti' || viewMode === 'havi') {
      await loadPeriodData(viewMode, periodAnchorDate);
    }
  };

  const handleMunkaszamClick = (munkaszam: string) => {
    if (canViewAllTimeTracking && includeAllUsers) {
      loadMunkaszamResztvevok(munkaszam);
      return;
    }

    loadIdoraforditasok(munkaszam);
  };

  const handleBackToMunkaszamok = async () => {
    if (selectedParticipant && selectedMunkaszam && canViewAllTimeTracking && includeAllUsers) {
      await loadMunkaszamResztvevok(selectedMunkaszam);
      return;
    }

    if (selectedParticipant && selectedMunkaszam) {
      await loadIdoraforditasok(selectedMunkaszam);
      return;
    }

    setViewMode('munkaszamok');
    setSelectedMunkaszam(null);
    setSelectedParticipant(null);
    setMunkaszamResztvevok([]);
    setFilteredMunkaszamResztvevok([]);
    setIdoraforditasok([]);
    setSearchFilter(null);
    setVisibleColumns(undefined);
  };

  const handleViewModeSwitch = (nextMode: ViewMode) => {
    if (nextMode === viewMode) {
      return;
    }

    setSearchFilter(null);
    setVisibleColumns(undefined);

    if (nextMode === 'munkaszamok') {
      loadMunkaszamok();
      return;
    }

    setViewMode(nextMode);
  };

  const handlePeriodShift = (direction: -1 | 1) => {
    setPeriodAnchorDate((previous) => {
      const nextDate = new Date(previous);
      if (viewMode === 'heti') {
        nextDate.setDate(nextDate.getDate() + direction * 7);
      } else {
        nextDate.setMonth(nextDate.getMonth() + direction);
      }
      return nextDate;
    });
  };

  const toLocalDateKey = (value: string | Date): string => {
    const sourceDate = value instanceof Date ? value : new Date(value);
    const year = sourceDate.getFullYear();
    const month = padLeft(sourceDate.getMonth() + 1, 2);
    const day = padLeft(sourceDate.getDate(), 2);
    return `${year}-${month}-${day}`;
  };

  const getMondayBasedDayIndex = (date: Date): number => {
    const day = date.getDay();
    return day === 0 ? 6 : day - 1;
  };

  const periodDays = useMemo(() => {
    if (viewMode !== 'heti' && viewMode !== 'havi') {
      return [] as { date: Date; inCurrentPeriod: boolean }[];
    }

    if (viewMode === 'heti') {
      const { start, end } = getWeekBounds(periodAnchorDate);
      const days: { date: Date; inCurrentPeriod: boolean }[] = [];
      const current = new Date(start);

      while (current <= end) {
        days.push({ date: new Date(current), inCurrentPeriod: true });
        current.setDate(current.getDate() + 1);
      }

      return days;
    }

    const { start: monthStart, end: monthEnd } = getMonthBounds(periodAnchorDate);
    const calendarStart = new Date(monthStart);
    calendarStart.setDate(calendarStart.getDate() - getMondayBasedDayIndex(calendarStart));

    const calendarEnd = new Date(monthEnd);
    const sundayBasedIndex = calendarEnd.getDay();
    const daysToSunday = sundayBasedIndex === 0 ? 0 : 7 - sundayBasedIndex;
    calendarEnd.setDate(calendarEnd.getDate() + daysToSunday);

    const days: { date: Date; inCurrentPeriod: boolean }[] = [];
    const current = new Date(calendarStart);
    current.setHours(0, 0, 0, 0);
    calendarEnd.setHours(23, 59, 59, 999);

    while (current <= calendarEnd) {
      days.push({
        date: new Date(current),
        inCurrentPeriod: current.getMonth() === periodAnchorDate.getMonth(),
      });
      current.setDate(current.getDate() + 1);
    }

    return days;
  }, [viewMode, periodAnchorDate]);

  const periodTitle = useMemo(() => {
    if (viewMode === 'heti') {
      const { start, end } = getWeekBounds(periodAnchorDate);
      return `${start.toLocaleDateString('hu-HU')} - ${end.toLocaleDateString('hu-HU')}`;
    }

    if (viewMode === 'havi') {
      return periodAnchorDate.toLocaleDateString('hu-HU', { year: 'numeric', month: 'long' });
    }

    return '';
  }, [viewMode, periodAnchorDate]);

  const groupedPeriodData = useMemo(() => {
    const groups: Record<string, IdoraforditasDto[]> = {};
    filteredIdoraforditasok.forEach((item) => {
      const key = toLocalDateKey(item.datum);
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(item);
    });

    return groups;
  }, [filteredIdoraforditasok]);

  const handleIdoraforditasClick = (idoraforditas: IdoraforditasDto) => {
    setSelectedItem(idoraforditas);
    setIsPanelOpen(true);
  };

  const handleResztvevoClick = (resztvevo: MunkaszamResztvevoOsszesitoDto) => {
    if (!selectedMunkaszam) {
      return;
    }

    const participantIdentifier = resztvevo.userId || resztvevo.felhasznaloNev;
    if (!participantIdentifier) {
      return;
    }

    loadIdoraforditasokByParticipantAndMunkaszam(participantIdentifier, selectedMunkaszam);
  };

  const handlePanelClose = () => {
    setIsPanelOpen(false);
    setTimeout(() => setSelectedItem(null), 500);
  };

  const formatColumnName = (key: string): string => {
    const columnNames: { [key: string]: string } = {
      id: 'ID',
      userId: 'Felhasználó azonosító',
      munkaszam: 'Munkaszám',
      munkaTargya: 'Munka tárgya',
      osszesOraszam: 'Összes óra',
      utolsoIdoraforditasDatum: 'Utolsó dátum',
      utolsoComment: 'Utolsó megjegyzés',
      datum: 'Dátum',
      bejegyzesTargya: 'Bejegyzés tárgya',
      idoraforditasErteke: 'Időráfordítás (óra)',
      munkaszamErteke: 'Munkaszám',
      felhasznaloNev: 'Felhasználó',
      workLocation: 'Helyszín',
      workType: 'Munka típusa',
    };
    return columnNames[key] || key;
  };

  const getVisibleColumns = (allColumns: string[], defaultColumns: string[]): string[] => {
    const baseColumns = defaultColumns.filter(column => allColumns.indexOf(column) !== -1);
    if (visibleColumns !== undefined) {
      const filteredColumns = visibleColumns.filter(column => allColumns.indexOf(column) !== -1);
      if (filteredColumns.length > 0) {
        return filteredColumns;
      }
      if (baseColumns.length > 0) {
        return baseColumns;
      }
      return allColumns.filter(column => column !== 'id');
    }
    return baseColumns;
  };

  const formatCellValue = (key: string, value: any): string => {
    if (value === null || value === undefined) return '';

    if (key === 'munkaszam' || key === 'munkaszamErteke') {
      const raw = String(value).trim().replace(/\s+/g, '');
      const match = raw.match(/^(\d{2,4})-(\d{1,3})\/(\d{1,})$/);
      if (match) {
        const year = Number(match[1]) % 100;
        const sorszam = Number(match[2]);
        const suffixRaw = match[3];
        const suffix = suffixRaw.length > 4 ? suffixRaw : padLeft(Number(suffixRaw), 4);
        return `${padLeft(year, 2)}-${padLeft(sorszam, 3)}/${suffix}`;
      }
      return raw;
    }

    if (key === 'osszesOraszam' || key === 'idoraforditasErteke') {
      return `${Number(value).toFixed(2)} óra`;
    }
    if (key === 'datum' || key === 'utolsoIdoraforditasDatum') {
      return new Date(value).toLocaleDateString('hu-HU');
    }
    if (key === 'workLocation') {
      return WORK_LOCATION_LABELS[value as WorkLocationType] || value;
    }
    return String(value);
  };

  const renderPeriodDayLabel = (date: Date): string => {
    const weekday = date.toLocaleDateString('hu-HU', { weekday: 'long' });
    const normalizedWeekday = weekday.charAt(0).toUpperCase() + weekday.slice(1);
    return `${normalizedWeekday} (${date.toLocaleDateString('hu-HU')})`;
  };

  const calendarWeekDayLabels = ['Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek', 'Szombat', 'Vasárnap'];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Controller Widget */}
      <ControllerWidget 
        selectedMenu="idoraforditasok"
        tableColumns={tableColumns}
        onSearch={handleSearch}
        onVisibleColumnsChange={setVisibleColumns}
        onAddRecord={handleAddRecord}
        onGetSuggestions={handleGetSuggestions}
        createPermission={Permissions.CreateTimeTracking}
      />

      <Card>
        <CardContent>
          {/* Vissza gomb és cím */}
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            {(viewMode === 'idoraforditasok' || viewMode === 'resztvevok') && (
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleBackToMunkaszamok}
                variant="outlined"
                size="small"
              >
                Vissza
              </Button>
            )}
            <Typography variant="h5" component="h2">
              {viewMode === 'munkaszamok' 
                ? 'Időráfordítások - Munkaszámok' 
                : viewMode === 'resztvevok'
                  ? `Munkaszám résztvevők - ${selectedMunkaszam || ''}`
                : viewMode === 'heti'
                  ? 'Időráfordítások - Heti nézet'
                  : viewMode === 'havi'
                    ? 'Időráfordítások - Havi nézet'
                : selectedParticipant 
                  ? `${selectedParticipant} időráfordításai - ${selectedMunkaszam}`
                  : `Időráfordítások - ${selectedMunkaszam}`}
            </Typography>
            {viewMode === 'idoraforditasok' && selectedParticipant && (
              <Chip
                label={selectedParticipant}
                size="small"
                color="primary"
                icon={<PersonIcon />}
              />
            )}
          </Box>

          <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <ButtonGroup size="small" variant="outlined">
              <Button
                variant={viewMode === 'munkaszamok' ? 'contained' : 'outlined'}
                onClick={() => handleViewModeSwitch('munkaszamok')}
              >
                Lista nézet
              </Button>
              <Button
                variant={viewMode === 'heti' ? 'contained' : 'outlined'}
                onClick={() => handleViewModeSwitch('heti')}
              >
                Heti nézet
              </Button>
              <Button
                variant={viewMode === 'havi' ? 'contained' : 'outlined'}
                onClick={() => handleViewModeSwitch('havi')}
              >
                Havi nézet
              </Button>
            </ButtonGroup>

            {canViewAllTimeTracking && (viewMode === 'munkaszamok' || viewMode === 'heti' || viewMode === 'havi') && (
              <FormControlLabel
                control={
                  <Switch
                    checked={includeAllUsers}
                    onChange={(event) => setIncludeAllUsers(event.target.checked)}
                  />
                }
                label="Mindenki időráfordítása"
              />
            )}
          </Box>

          {(viewMode === 'heti' || viewMode === 'havi') && (
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <IconButton onClick={() => handlePeriodShift(-1)} aria-label="Előző időszak">
                <ChevronLeftIcon />
              </IconButton>

              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {periodTitle}
              </Typography>

              <IconButton onClick={() => handlePeriodShift(1)} aria-label="Következő időszak">
                <ChevronRightIcon />
              </IconButton>
            </Box>
          )}

          {/* Munkaszámok táblázat */}
          {viewMode === 'munkaszamok' && (
            (() => {
              const allColumns: string[] = filteredMunkaszamok.length > 0
                ? Object.keys(filteredMunkaszamok[0])
                : ['munkaszam', 'munkaTargya', 'osszesOraszam', 'utolsoIdoraforditasDatum', 'utolsoComment'];
              const columnsToShow = getVisibleColumns(allColumns, ['munkaszam', 'munkaTargya', 'osszesOraszam', 'utolsoIdoraforditasDatum', 'utolsoComment']);

              return (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columnsToShow.map((column) => (
                      <TableCell key={column} align={column === 'osszesOraszam' ? 'right' : 'left'}>
                        <strong>{formatColumnName(column)}</strong>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredMunkaszamok.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={Math.max(columnsToShow.length, 1)} align="center">
                        <Typography variant="body2" color="text.secondary">
                          Nincsenek időráfordítások
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredMunkaszamok.map((item, index) => (
                      <TableRow
                        key={index}
                        hover
                        onClick={() => handleMunkaszamClick(item.munkaszam)}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                      >
                        {columnsToShow.map((column) => {
                          if (column === 'munkaszam') {
                            return (
                              <TableCell key={column}>
                                <Chip label={item.munkaszam} color="primary" size="small" />
                              </TableCell>
                            );
                          }

                          if (column === 'osszesOraszam') {
                            return (
                              <TableCell key={column} align="right">
                                <Typography variant="body2" fontWeight="bold">
                                  {formatCellValue('osszesOraszam', item.osszesOraszam)}
                                </Typography>
                              </TableCell>
                            );
                          }

                          if (column === 'utolsoComment') {
                            return (
                              <TableCell key={column}>
                                <Typography 
                                  variant="body2" 
                                  sx={{
                                    maxWidth: 300,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {item.utolsoComment}
                                </Typography>
                              </TableCell>
                            );
                          }

                          return (
                            <TableCell key={column}>
                              {formatCellValue(column, (item as any)[column])}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
              );
            })()
          )}

          {viewMode === 'resztvevok' && (
            (() => {
              const allColumns: string[] = filteredMunkaszamResztvevok.length > 0
                ? getResztvevoViewColumns(Object.keys(filteredMunkaszamResztvevok[0]))
                : ['felhasznaloNev', 'osszesOraszam', 'utolsoIdoraforditasDatum', 'utolsoComment'];
              const columnsToShow = getVisibleColumns(allColumns, ['felhasznaloNev', 'osszesOraszam', 'utolsoIdoraforditasDatum', 'utolsoComment']);

              return (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {columnsToShow.map((column) => (
                          <TableCell key={column} align={column === 'osszesOraszam' ? 'right' : 'left'}>
                            <strong>{formatColumnName(column)}</strong>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredMunkaszamResztvevok.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={Math.max(columnsToShow.length, 1)} align="center">
                            <Typography variant="body2" color="text.secondary">
                              Nincs időráfordítás ehhez a munkaszámhoz
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredMunkaszamResztvevok.map((item, index) => (
                          <TableRow
                            key={`${item.userId}-${index}`}
                            hover
                            onClick={() => handleResztvevoClick(item)}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: 'action.hover' }
                            }}
                          >
                            {columnsToShow.map((column) => (
                              <TableCell key={column} align={column === 'osszesOraszam' ? 'right' : 'left'}>
                                {column === 'osszesOraszam' ? (
                                  <Typography variant="body2" fontWeight="bold">
                                    {formatCellValue(column, (item as any)[column])}
                                  </Typography>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    sx={column === 'utolsoComment' ? {
                                      maxWidth: 360,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    } : undefined}
                                  >
                                    {formatCellValue(column, (item as any)[column])}
                                  </Typography>
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              );
            })()
          )}

          {/* Időráfordítások táblázat */}
          {viewMode === 'idoraforditasok' && (
            (() => {
              const allColumns: string[] = filteredIdoraforditasok.length > 0
                ? getDetailedViewColumns(Object.keys(filteredIdoraforditasok[0]))
                : ['datum', 'bejegyzesTargya', 'idoraforditasErteke', 'workLocation', 'workType'];
              const columnsToShow = getVisibleColumns(allColumns, ['datum', 'bejegyzesTargya', 'idoraforditasErteke', 'workLocation', 'workType']);

              return (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columnsToShow.map((column) => (
                      <TableCell key={column} align={column === 'idoraforditasErteke' ? 'right' : 'left'}>
                        <strong>{formatColumnName(column)}</strong>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredIdoraforditasok.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={Math.max(columnsToShow.length, 1)} align="center">
                        <Typography variant="body2" color="text.secondary">
                          Nincsenek időráfordítások ehhez a munkaszámhoz
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredIdoraforditasok.map((item) => (
                      <TableRow
                        key={item.id}
                        hover
                        onClick={() => handleIdoraforditasClick(item)}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                      >
                        {columnsToShow.map((column) => {
                          if (column === 'bejegyzesTargya') {
                            return (
                              <TableCell key={column}>
                                <Typography 
                                  variant="body2"
                                  sx={{
                                    maxWidth: 400,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {item.bejegyzesTargya}
                                </Typography>
                              </TableCell>
                            );
                          }

                          if (column === 'idoraforditasErteke') {
                            return (
                              <TableCell key={column} align="right">
                                <Typography variant="body2" fontWeight="bold">
                                  {formatCellValue('idoraforditasErteke', item.idoraforditasErteke)}
                                </Typography>
                              </TableCell>
                            );
                          }

                          return (
                            <TableCell key={column}>
                              {formatCellValue(column, (item as any)[column])}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
              );
            })()
          )}

          {(viewMode === 'heti' || viewMode === 'havi') && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 1 }}>
                {calendarWeekDayLabels.map((label) => (
                  <Paper key={label} variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
                    <Typography variant="subtitle2" fontWeight={700}>{label}</Typography>
                  </Paper>
                ))}
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 1 }}>
                {periodDays.map((day) => {
                  const dayKey = toLocalDateKey(day.date);
                  const dayItems = groupedPeriodData[dayKey] || [];
                  const dayTotal = dayItems.reduce((acc, item) => acc + Number(item.idoraforditasErteke), 0);
                  const isExpanded = !!expandedDayKeys[dayKey];
                  const visibleItems = isExpanded ? dayItems : dayItems.slice(0, 3);
                  const remainingCount = Math.max(dayItems.length - 3, 0);

                  return (
                    <Paper
                      key={dayKey}
                      variant="outlined"
                      sx={{
                        p: 1,
                        minHeight: 170,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.75,
                        opacity: day.inCurrentPeriod ? 1 : 0.45,
                        backgroundColor: day.inCurrentPeriod ? 'background.paper' : 'action.hover',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          {day.date.toLocaleDateString('hu-HU', { month: '2-digit', day: '2-digit' })}
                        </Typography>
                        <Chip size="small" color="primary" label={`${dayTotal.toFixed(2)} óra`} />
                      </Box>

                      {viewMode === 'heti' && (
                        <Typography variant="caption" color="text.secondary">
                          {renderPeriodDayLabel(day.date).split('(')[0].trim()}
                        </Typography>
                      )}

                      <Divider />

                      {visibleItems.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          Nincs bejegyzés
                        </Typography>
                      ) : (
                        visibleItems.map((item) => (
                          <Box
                            key={item.id}
                            onClick={() => handleIdoraforditasClick(item)}
                            sx={{
                              p: 0.75,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: 'action.hover' }
                            }}
                          >
                            <Typography variant="caption" fontWeight={700} sx={{ display: 'block' }}>
                              {item.munkaszamErteke}
                            </Typography>
                            {includeAllUsers && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {item.felhasznaloNev || '-'}
                              </Typography>
                            )}
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {item.bejegyzesTargya}
                            </Typography>
                            <Typography variant="caption" fontWeight={700} sx={{ display: 'block', textAlign: 'right' }}>
                              {formatCellValue('idoraforditasErteke', item.idoraforditasErteke)}
                            </Typography>
                          </Box>
                        ))
                      )}

                      {dayItems.length > 3 && (
                        <Button
                          size="small"
                          variant="text"
                          sx={{ alignSelf: 'flex-start', px: 0.5, minWidth: 0, textTransform: 'none' }}
                          onClick={() => {
                            setExpandedDayKeys((previous) => ({
                              ...previous,
                              [dayKey]: !previous[dayKey],
                            }));
                          }}
                        >
                          {isExpanded ? 'Bezárás' : `+${remainingCount} további bejegyzés`}
                        </Button>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Detail Panel az időráfordítás részleteinek megtekintéséhez */}
      {selectedItem && (
        <DetailPanel
          isOpen={isPanelOpen}
          data={selectedItem}
          columns={includeAllUsers ? ['felhasznaloNev', 'munkaszamErteke', 'datum', 'bejegyzesTargya', 'idoraforditasErteke', 'workLocation', 'workType'] : ['munkaszamErteke', 'datum', 'bejegyzesTargya', 'idoraforditasErteke', 'workLocation', 'workType']}
          onClose={handlePanelClose}
          onEdit={handleEdit}
          onDelete={handleDelete}
          formatColumnName={formatColumnName}
          formatCellValue={formatCellValue}
          readonlyFields={includeAllUsers ? ['felhasznaloNev', 'munkaszamErteke'] : ['munkaszamErteke']}
          editPermission={Permissions.EditTimeTracking}
          deletePermission={Permissions.DeleteTimeTracking}
        />
      )}

      {/* Add időráfordítás Dialog */}
      <AddIdoraforditasDialog
        open={addDialogOpen}
        onClose={handleDialogClose}
        onSuccess={handleDialogSuccess}
        munkaszam={selectedMunkaszam}
      />
    </Box>
  );
};

export default IdoraforditasokContent;
